import { useState } from 'react';
import { useBackgroundProcessing } from '@/context/BackgroundProcessingContext';
import { Loader2, CheckCircle2, XCircle, X, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';

/**
 * GLOBAL PROCESSING INDICATOR
 * 
 * Shows in the header/navbar when background processing is active.
 * Persists across page navigation.
 */
const GlobalProcessingIndicator = () => {
  const { activeJobs, completedJobs, failedJobs, hasActiveJobs, cancelJob, clearJob, activeJobCount } = useBackgroundProcessing();
  const [expanded, setExpanded] = useState(false);

  const totalJobs = activeJobCount + Object.keys(completedJobs).length + Object.keys(failedJobs).length;

  if (totalJobs === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Collapsed View - Badge */}
      <div 
        className="bg-slate-800 border border-slate-600 rounded-lg shadow-lg cursor-pointer overflow-hidden"
        onClick={() => setExpanded(!expanded)}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 gap-3">
          <div className="flex items-center gap-2">
            {hasActiveJobs ? (
              <Loader2 className="h-4 w-4 animate-spin text-yellow-500" />
            ) : Object.keys(failedJobs).length > 0 ? (
              <XCircle className="h-4 w-4 text-red-500" />
            ) : (
              <CheckCircle2 className="h-4 w-4 text-green-500" />
            )}
            <span className="text-sm font-medium text-white">
              {hasActiveJobs 
                ? `Processing ${activeJobCount} request${activeJobCount > 1 ? 's' : ''}` 
                : Object.keys(failedJobs).length > 0 
                  ? 'Processing failed'
                  : 'Processing complete'
              }
            </span>
          </div>
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-slate-400" />
          ) : (
            <ChevronUp className="h-4 w-4 text-slate-400" />
          )}
        </div>

        {/* Expanded View - Job Details */}
        {expanded && (
          <div className="border-t border-slate-600 max-h-80 overflow-y-auto">
            {/* Active Jobs */}
            {Object.values(activeJobs).map(job => (
              <div key={job.jobId} className="px-4 py-3 border-b border-slate-700 last:border-b-0">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-white font-medium truncate max-w-[200px]">
                    {job.caseName}
                  </span>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-6 w-6 p-0 text-slate-400 hover:text-red-500"
                    onClick={(e) => { e.stopPropagation(); cancelJob(job.jobId); }}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <Progress value={job.progress || 0} className="flex-1 h-1.5" />
                  <span className="text-xs text-slate-400">{job.status}</span>
                </div>
              </div>
            ))}

            {/* Completed Jobs */}
            {Object.values(completedJobs).map(job => (
              <div key={job.jobId} className="px-4 py-3 border-b border-slate-700 last:border-b-0 bg-green-900/20">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    <span className="text-sm text-white truncate max-w-[180px]">{job.caseName}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs text-green-400 hover:text-green-300 h-6 px-2"
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        window.location.href = `/dashboard/cases/${job.caseId}`;
                      }}
                    >
                      View
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 w-6 p-0 text-slate-400 hover:text-white"
                      onClick={(e) => { e.stopPropagation(); clearJob(job.jobId); }}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}

            {/* Failed Jobs */}
            {Object.values(failedJobs).map(job => (
              <div key={job.jobId} className="px-4 py-3 border-b border-slate-700 last:border-b-0 bg-red-900/20">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <XCircle className="h-4 w-4 text-red-500" />
                    <div>
                      <span className="text-sm text-white truncate max-w-[180px] block">{job.caseName}</span>
                      {job.error && (
                        <span className="text-xs text-red-400">{job.error}</span>
                      )}
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-6 w-6 p-0 text-slate-400 hover:text-white"
                    onClick={(e) => { e.stopPropagation(); clearJob(job.jobId); }}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GlobalProcessingIndicator;
